/**
 * The run chrome — status pill, composer lock, Stop-vs-Send — for a protocol-v1 run.
 *
 * WHAT WAS BROKEN. `MultiAgentRoute` derives all three from `multiAgentStore.status`, and that field
 * is written by the LEGACY stream's handlers. On the v1 path `selectRunStreams` deliberately does not
 * open the legacy stream, so nothing ever moved the field off the `"starting"` that `startRun` wrote:
 * the run finished, the SSE stream closed, and the header still said Running with the composer, the
 * mic and the attachment controls disabled and the Send button stuck as Stop. There was no way back
 * except a reload.
 *
 * Pure, and keyed on PRIMITIVES rather than on `RunState.run`, because the caller must not subscribe
 * to an object whose identity changes on every frame (see `selectCancellable`).
 */
import type { RunState } from "./protocol";

/** The `status.constants` vocabulary the header's dot map and the composer already speak. */
export type ChromeStatus =
  | "idle"
  | "starting"
  | "running"
  | "awaiting_approval"
  | "done"
  | "failed"
  | "cancelled"
  | "expired";

/**
 * Protocol `RunStatus` → the chrome's vocabulary.
 *
 * `input_required` reads as "Awaiting approval" because that is what a parked run is waiting for, and
 * `expired` keeps its own name: mapping it onto `done` or `cancelled` would tell the reader the run
 * ended normally or that they stopped it, and neither is true — a TTL lapsed and the answer is a new
 * run (contract §5.3).
 */
const STATUS: Record<RunState["run"]["status"], ChromeStatus> = {
  queued: "starting",
  running: "running",
  input_required: "awaiting_approval",
  completed: "done",
  failed: "failed",
  cancelled: "cancelled",
  expired: "expired",
};

export interface WeaveChrome {
  status: ChromeStatus;
  /**
   * Is the run still producing? Locks the composer and swaps Send for Stop.
   *
   * `input_required` is deliberately NOT in flight. A parked run is waiting for the user, and the
   * contract is explicit that their next message is always a valid answer (§5.3) — locking the
   * composer there is what turns a question into a dead end.
   */
  inFlight: boolean;
  /**
   * May the reader stop it? Straight from `RunState.run.cancellable`, which the backend computes.
   *
   * Read rather than inferred: §8.2 rule 8 puts action legality on the server, and a client that
   * decided this itself would offer Stop for a run the `POST /cancel` endpoint refuses.
   */
  cancellable: boolean;
}

const IDLE: WeaveChrome = { status: "idle", inFlight: false, cancellable: false };

export function weaveChrome(
  status: RunState["run"]["status"] | null | undefined,
  cancellable: boolean,
): WeaveChrome {
  if (!status) return IDLE;
  /*
   * An unknown status reads as `running`, which is the contract's own documented fallback for this
   * field (§12 versioning rules). It is the safe direction: a new terminal value would otherwise
   * read as `idle` and let the composer fire a second run into a live one.
   */
  const mapped = STATUS[status] ?? "running";
  return {
    status: mapped,
    inFlight: status === "queued" || status === "running",
    cancellable,
  };
}

/** The legacy store's own account of a run. A DIFFERENT vocabulary from the protocol's. */
export interface LegacyRunChrome {
  readOnly: boolean;
  status: string | null | undefined;
}

/**
 * The legacy arm: `multiAgentStore.status`, which `useRunStream` writes, in the chrome's words.
 *
 * Its vocabulary IS the chrome's already (it is what `STATUS_DOT_MAP` has always been keyed on), so
 * there is nothing to map — only the in-flight set to name, which is `LIVE_STATUSES` and the reason
 * a permanently-`"starting"` store deadlocked the composer.
 *
 * `cancellable` IS `inFlight` here, and that asymmetry with the v1 arm is the point: §8.2 rule 8
 * puts action legality on the server, and the legacy surface HAS no such field. Inferring it is
 * wrong on v1 (the backend may refuse a cancel the client would offer) and right on legacy (there
 * is nothing else to ask, and Stop worked this way for every user before `/weave` flipped).
 */
function legacyChrome(legacy: LegacyRunChrome): WeaveChrome {
  const status = (legacy.status || "idle") as ChromeStatus;
  const inFlight =
    !legacy.readOnly &&
    (legacy.status === "running" ||
      legacy.status === "starting" ||
      legacy.status === "planning" ||
      legacy.status === "awaiting_approval");
  return { status, inFlight, cancellable: inFlight };
}

/**
 * ── THE ONE OWNER OF "WHAT IS THIS RUN DOING", ON EITHER PROTOCOL ────────────────────────────
 *
 * Both stores describe a run and only one of them is being written at a time: `runStateStore` when
 * the server serves the protocol, `multiAgentStore` when it serves legacy. Which one to believe is
 * `protocol/negotiation.ts`'s answer, and this is the single place it is applied to the chrome.
 *
 * IT EXISTS BECAUSE THE SPLIT ANSWER SHIPPED ONCE ALREADY, in both directions. `MultiAgentRoute`
 * derived the header pill, the composer lock and Stop-vs-Send from `weaveChrome` alone — v1-only —
 * so on a legacy run the Stop button never rendered, the attach and mic controls never disabled,
 * and Send was dead behind a guard that DID know about legacy. One fact, two readers, opposite
 * bugs on each path. Anything asking "is this run in flight" calls this, including
 * `composerInFlight` below, which is now a named view of it rather than a second rule.
 */
export function runChrome(
  protocolV1: boolean,
  protocolStatus: RunState["run"]["status"] | null | undefined,
  protocolCancellable: boolean,
  legacy: LegacyRunChrome,
): WeaveChrome {
  return protocolV1 ? weaveChrome(protocolStatus, protocolCancellable) : legacyChrome(legacy);
}

/**
 * May the composer accept a new message right now?
 *
 * ── WHY THIS IS A FUNCTION AND NOT AN `if` IN THE HANDLER ────────────────────
 *
 * It is the whole of the follow-up deadlock. After any completed answer the composer stopped
 * accepting messages — Enter dead, Send dead, no request, no console error — until the page was
 * refreshed. The cause was a guard that asked the LEGACY store on the v1 path: `startRun()` writes
 * `status: "starting"` there on every send, but v1 never subscribes the legacy stream
 * (`selectRunStreams` hands the run to the protocol stream instead), so nothing ever advanced that
 * status past `"starting"` — and `"starting"` counts as in-flight, permanently.
 *
 * As a predicate it can be given the state that produced the bug and asked what the composer does.
 * A component test cannot: the deadlock only appears on the SECOND send, after a terminal frame.
 *
 * DELEGATES to `runChrome` rather than restating the rule: the composer's idea of in-flight and the
 * Stop button's must not be able to disagree, which they did for the whole of the legacy path.
 * Kept as its own export because the call site reads the fresh store inside the send handler and
 * "may I send" is the question it is actually asking.
 */
export function composerInFlight(
  protocolV1: boolean,
  protocolStatus: RunState["run"]["status"] | null | undefined,
  legacy: LegacyRunChrome,
): boolean {
  // `false` for cancellable: it has no bearing on in-flight, and the caller has no reason to know it.
  return runChrome(protocolV1, protocolStatus, false, legacy).inFlight;
}
